import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import {
    LocationDetailsDto,
    Lotam,
    MissionCrewDetailsDto,
    MissionNoticeDto,
    NoticeDto,
    Organisation,
    UserService
} from '@flyfreely-portal-ui/flyfreely';
import { MODAL_OPTIONS } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { MissionNoticesDataService } from '@flyfreely-portal-ui/notices-shared';
import { LocationBearing } from '@flyfreely-portal-ui/ui';
import { MissionNoticeAcknowledgementDto } from 'libs/flyfreely/src/lib/model/api/missionNoticeAcknowledgementDto';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MissionLotamDetailsComponent } from '../mission-lotam-details/mission-lotam-details.component';
import {
    MissionNoticeAcknowledger,
    resolveMissionNoticeAcknowledgers
} from '../mission-notice-acknowledgers';

/**
 * The most acknowledgers that are named before the summary is shortened to
 * "A, B, and N others".
 */
const ACKNOWLEDGEMENT_SUMMARY_LIMIT = 3;

export interface AcknowledgerSummary {
    visibleNames: string[];
    othersCount: number;
    /** The named part of the summary, e.g. "John Doe, Jane Doe, and " */
    namesText: string;
    /** The counted part of the summary, e.g. "3 others" */
    othersText: string;
}

/**
 * Builds a summary such as "John Doe, Jane Doe, and 3 others". Acknowledgers
 * that are not on the mission crew can't be named, so they are always counted.
 */
export function summariseAcknowledgers(
    names: string[],
    unnamedCount: number,
    showAll: boolean
): AcknowledgerSummary {
    const total = names.length + unnamedCount;
    const visibleNames =
        showAll || total <= ACKNOWLEDGEMENT_SUMMARY_LIMIT
            ? names
            : names.slice(0, ACKNOWLEDGEMENT_SUMMARY_LIMIT - 1);
    const othersCount = total - visibleNames.length;

    if (othersCount === 0) {
        const namesText =
            visibleNames.length <= 2
                ? visibleNames.join(' and ')
                : `${visibleNames.slice(0, -1).join(', ')}, and ${
                      visibleNames[visibleNames.length - 1]
                  }`;
        return { visibleNames, othersCount, namesText, othersText: '' };
    }
    if (visibleNames.length === 0) {
        return {
            visibleNames,
            othersCount,
            namesText: '',
            othersText: `${othersCount} ${
                othersCount === 1 ? 'person' : 'people'
            }`
        };
    }
    return {
        visibleNames,
        othersCount,
        namesText:
            visibleNames.length === 1
                ? `${visibleNames[0]} and `
                : `${visibleNames.join(', ')}, and `,
        othersText: `${othersCount} ${othersCount === 1 ? 'other' : 'others'}`
    };
}

@Component({
    selector: 'mission-lotam-summary',
    templateUrl: './mission-lotam-summary.component.html',
    styleUrls: ['./mission-lotam-summary.component.scss']
})
export class MissionLotamSummaryComponent
    implements OnInit, OnDestroy, OnChanges
{
    @Input() lotam: NoticeDto;
    @Input() acknowledgementList: Array<MissionNoticeAcknowledgementDto>;
    @Input() missionId: number;
    @Input() missionLocation: LocationDetailsDto;
    @Input() organisation: Organisation;
    /** Used to resolve the names of the people that have acknowledged. */
    @Input() missionCrewDetails: MissionCrewDetailsDto[] = [];
    @Input() locationBearing: LocationBearing;
    @Input() isReadOnly = false;
    @Input() status: MissionNoticeDto.Status;
    private ngUnsubscribe$ = new Subject<void>();

    isAcknowledged = false;
    acknowledgementTime?: string;

    acknowledgers: MissionNoticeAcknowledger[] = [];
    acknowledgerNames: string[] = [];
    unnamedAcknowledgerCount = 0;
    showAllAcknowledgers = false;

    constructor(
        private userService: UserService,
        private modalService: BsModalService,
        private missionNoticesDataService: MissionNoticesDataService
    ) {}

    ngOnInit(): void {
        this.refreshAcknowledged();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            'acknowledgementList' in changes ||
            'missionCrewDetails' in changes
        ) {
            this.refreshAcknowledged();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    get buttonText(): string {
        if (
            this.isReadOnly ||
            this.status !== MissionNoticeDto.Status.APPLICABLE
        ) {
            return 'View';
        }
        return this.isAcknowledged ? 'View' : 'Acknowledge';
    }

    refreshAcknowledged() {
        const currentUserId = this.userService.getCurrentUser().id;
        const maybeAcknowledgement = this.acknowledgementList?.find(
            item => item.personId === currentUserId
        );
        this.acknowledgementTime = maybeAcknowledgement?.acknowledgementTime;
        this.isAcknowledged = maybeAcknowledgement != null;
        this.refreshAcknowledgers();
    }

    get acknowledgerSummary() {
        return summariseAcknowledgers(
            this.acknowledgerNames,
            this.unnamedAcknowledgerCount,
            this.showAllAcknowledgers
        );
    }

    /** Only named acknowledgers can be revealed by expanding the summary. */
    get canExpandAcknowledgers() {
        return (
            this.acknowledgerSummary.visibleNames.length <
            this.acknowledgerNames.length
        );
    }

    get canCollapseAcknowledgers() {
        return (
            this.showAllAcknowledgers &&
            this.acknowledgerNames.length + this.unnamedAcknowledgerCount >
                ACKNOWLEDGEMENT_SUMMARY_LIMIT
        );
    }

    toggleShowAllAcknowledgers() {
        this.showAllAcknowledgers = !this.showAllAcknowledgers;
    }

    private refreshAcknowledgers() {
        this.acknowledgers = resolveMissionNoticeAcknowledgers(
            this.acknowledgementList,
            this.missionCrewDetails,
            this.userService.getCurrentUser()
        );
        this.acknowledgerNames = this.acknowledgers
            .map(a => a.name)
            .filter((name): name is string => name != null);
        this.unnamedAcknowledgerCount =
            this.acknowledgers.length - this.acknowledgerNames.length;
    }

    viewLotamDetail() {
        const dialogue = this.modalService.show<MissionLotamDetailsComponent>(
            MissionLotamDetailsComponent,
            {
                ...MODAL_OPTIONS,
                class: 'modal-lg',
                initialState: {
                    lotam: {
                        ...this.lotam,
                        isAcknowledged: this.isAcknowledged,
                        currentUserAcknowledgementTime:
                            this.acknowledgementTime,
                        availableActions: {
                            hasDelete: false,
                            canDelete: false,
                            hasEdit: false,
                            canEdit: false
                        }
                    } as Lotam,
                    missionId: this.missionId,
                    missionLocation: this.missionLocation,
                    organisation: this.organisation,
                    isAcknowledgedInMission: this.isAcknowledged,
                    acknowledgers: this.acknowledgers,
                    isReadOnly:
                        this.isReadOnly ||
                        this.status !== MissionNoticeDto.Status.APPLICABLE,
                    status: this.status
                },
                providers: [
                    {
                        provide: MissionNoticesDataService,
                        useValue: this.missionNoticesDataService
                    }
                ]
            }
        );
        dialogue.content.acknowledge
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(dialogue.onHide))
            .subscribe(acknowledgedPerson => {
                this.acknowledgementList.push(acknowledgedPerson);
                this.refreshAcknowledged();
            });
    }

    protected readonly MissionNoticeDto = MissionNoticeDto;
}
